import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'

import { LegalFooter } from '../components/layout/LegalFooter'
import { Seo } from '../shared/lib/seo'

type StaticPageSection = {
  id: string
  label: string
}

type StaticPageProps = {
  title: string
  description: string
  path: string
  eyebrow?: string
  updatedLabel?: string
  sections?: StaticPageSection[]
  children: React.ReactNode
}

export function StaticPage({
  title,
  description,
  path,
  eyebrow,
  updatedLabel,
  sections = [],
  children,
}: StaticPageProps) {
  return (
    <main className="min-h-0 flex-1 overflow-y-auto bg-slate-50">
      <Seo title={title} description={description} path={path} />

      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 md:px-6 md:py-8">
        <header className="overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.14),_transparent_38%),linear-gradient(135deg,_rgba(255,255,255,1),_rgba(248,250,252,1))] px-6 py-7 md:px-8 md:py-9">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="max-w-3xl">
                {eyebrow ? (
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">
                    {eyebrow}
                  </p>
                ) : null}
                <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
                  {title}
                </h1>
                <p className="mt-3 text-sm leading-7 text-slate-600 md:text-[15px]">
                  {description}
                </p>
              </div>

              <div className="rounded-[24px] border border-white/70 bg-white/80 px-4 py-3 text-sm text-slate-600 shadow-sm backdrop-blur">
                <p className="font-semibold text-slate-900">Fukodrom</p>
                <p>{updatedLabel ?? 'Velja za trenutno različico storitve.'}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 px-6 py-4 text-sm text-slate-600 md:flex-row md:items-center md:justify-between md:px-8">
            <div className="flex flex-wrap items-center gap-2">
              <Link to="/" className="font-medium text-slate-500 transition hover:text-slate-900">
                Domov
              </Link>
              <ChevronRight size={16} className="text-slate-300" />
              <span className="font-medium text-slate-900">{title}</span>
            </div>

            {sections.length ? (
              <nav aria-label="Kazalo strani" className="overflow-x-auto">
                <div className="flex min-w-max gap-2">
                  {sections.map((section) => (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800"
                    >
                      {section.label}
                    </a>
                  ))}
                </div>
              </nav>
            ) : null}
          </div>
        </header>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px] lg:items-start">
          <article className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm md:p-8">
            <div className="prose prose-slate max-w-none prose-headings:scroll-mt-28 prose-headings:tracking-tight prose-h2:mt-10 prose-h2:text-2xl prose-h3:mt-6 prose-h3:text-lg prose-p:text-slate-700 prose-li:text-slate-700 prose-strong:text-slate-900 prose-a:text-emerald-700">
              {children}
            </div>
          </article>

          <aside className="lg:sticky lg:top-6">
            <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">
                Hitri pregled
              </p>
              <div className="mt-4 space-y-4 text-sm leading-6 text-slate-600">
                <p>
                  Stran je napisana za dejanski način delovanja Fukodroma: račun, e-pošta,
                  skupnostna vsebina, moderiranje, first-party analitika in forumski del.
                </p>
                <p>
                  Če potrebujete dostop, popravek ali izbris podatkov, pišite na{' '}
                  <a href="mailto:privacy@fukodrom.com" className="font-medium text-emerald-700">
                    privacy@fukodrom.com
                  </a>
                  .
                </p>
              </div>

              {sections.length ? (
                <div className="mt-5 border-t border-slate-200 pt-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Na tej strani
                  </p>
                  <ul className="mt-3 space-y-2 text-sm">
                    {sections.map((section) => (
                      <li key={section.id}>
                        <a href={`#${section.id}`} className="text-slate-600 transition hover:text-slate-900">
                          {section.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </aside>
        </div>

        <LegalFooter />
      </div>
    </main>
  )
}
